import {
  Button,
  CreateButton,
  EditButton,
  List,
  RefreshButton,
  SaveButton,
  Select,
  Space,
  Table,
  Tag,
  useDrawerForm,
  useModal,
  useTable
} from '@pankod/refine-antd'
import { CrudFilters, GetOneResponse, HttpError, useSelect, useTranslate } from '@pankod/refine-core'
import { QueryObserverResult } from '@tanstack/react-query'
import { AdviceModal } from 'components/AdviceModal/AdviceModal'
import { BlockingAdviceModal } from 'components/BlockingAdviceModal/BlockingAdviceModal'
import { PendingWarehouseNoteModal } from 'components/orders/PendingWarehouseNoteModal'
import { ButtonLogicDelete } from 'components/DeleteModal/buttonLogicDelete'
import { DeleteModal } from 'components/DeleteModal/modal'
import { SupplementActions } from 'components/supplement/actions'
import { SendEmailButton } from 'components/supplement/send_email_button'
import { definitions, Order, Supplement } from 'interfaces'
import moment from 'moment'
import { ProcessModal } from 'pages/shipments/processModal'
import { useEffect, useState } from 'react'
import { ADMITTED_STATUS, API_URL, checkUserPermission, DEFAULT_DATE_TIME_FORMAT, ORDER_STATUS_ID_PENDING_PAYMENT, ORDER_STATUS_ID_REQUESTED, PENDING_REFUND_PAYMENT_STATUS_ID, PROCESSED_STATUS, supabaseClient, SUPPLEMENT_PREDEFINED_OPTIONS, SUPPLEMENT_STATUS, viewTicket } from 'utility'
import { capitalizeFirstLetter } from 'utility/capitalizeFirstLetter'
import { getColorLabel } from 'utility/colorLabel'
import { currency } from 'utility/currency'
import { getWarehousePreparationBlock, getWarehouseVolumetricBlock, hasWarehousePreparationNotice, isWarehouseVolumetricEditable, warehousePreparationAdviceKey } from 'utility/warehousePreparationGate'
import { getWarehouseReviewDecision } from 'utility/warehouseReview'
import { hasUncheckedWarehouseReviewItems } from 'utility/warehouseReviewChecklist'
// import { GroupedSupplementActions } from 'components/supplement/groupedSupplementForm'

type SupplementsProps = {
  order: definitions['Order'] | undefined;
  orderQuery: QueryObserverResult<GetOneResponse<Order>, unknown>
  setSupQuery?: any
  refetchRef?: any
  warehouseMode?: boolean
};

export const Supplements: React.FC<SupplementsProps> = ({ order, orderQuery, setSupQuery, refetchRef, warehouseMode = false }) => {
  const { modalProps, show, close } = useModal()
  const { modalProps: modalPropsAdvice, show: showAdvice, close: closeAdvice } = useModal()
  const { modalProps: modalPropsBlockingAdvice, show: openBlockingAdvice, close: closeBlockingAdvice } = useModal()
  const { modalProps: modalPropsPendingWarehouseNote, show: showPendingWarehouseNote, close: closePendingWarehouseNote } = useModal()
  const t = useTranslate()

  // const [allSupplements, setAllSupplements] = useState<any>([])
  const [id, setId] = useState(0)
  const { modalProps: modalPropsProcess, show: showProcess, close: closeProcess } = useModal()
  const [loading, setLoading] = useState(false)
  const [errorSaving, setErrorSaving] = useState<any>(null)
  const [adviseText, setAdviseText] = useState<string>('')
  const [blockingAdviceText, setBlockingAdviceText] = useState<string>('')
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [automaticallyAdmittedVolumetric, setAutomaticallyAdmittedVolumetric] = useState<any>(null)
  const [manualPreparationSupplement, setManualPreparationSupplement] = useState<any>(null)
  const [preparingShipment, setPreparingShipment] = useState(false)
  const [preparedVolumetricId, setPreparedVolumetricId] = useState<number | null>(null)
  // The Warehouse screen is already role-gated at its route.  Passing its
  // mode through is more reliable than re-reading a cached user profile while
  // a newly admitted volumetric supplement is being saved.
  const canAutomaticallyPrepareVolumetric = warehouseMode
  const warehouseVolumetricBlock = warehouseMode ? getWarehouseVolumetricBlock(order) : null
  const warehouseVolumetricBlockedMessage = t(warehousePreparationAdviceKey(order, 'supplement'))

  const showBlockingAdvice = (text: string) => {
    setBlockingAdviceText(text)
    openBlockingAdvice()
  }

  const canOpenWarehouseVolumetric = async (): Promise<boolean> => {
    if (!warehouseMode || !order?.id) return true

    try {
      const [decisionResult, itemsResult] = await Promise.allSettled([
        getWarehouseReviewDecision(order.id),
        supabaseClient.from('items')
          .select('disabled, deleted, delivered_warehouse')
          .eq('order_id', order.id)
          .eq('deleted', false)
      ])
      // If the decision cannot be read, use the conservative review path. A
      // temporary reader failure must never become a separate blocking rule.
      if (itemsResult.status !== 'fulfilled' || itemsResult.value.error || !itemsResult.value.data) return true
      const decision = decisionResult.status === 'fulfilled' ? decisionResult.value : null
      if (hasUncheckedWarehouseReviewItems(decision, itemsResult.value.data)) {
        showBlockingAdvice(t('supplement.warehouseBlock.reviewChecklist'))
        return false
      }
      return true
    } catch {
      return true
    }
  }

  const permFilters = [
    {
      field: 'order_id',
      operator: 'eq',
      value: order?.id
    },
    {
      field: 'deleted',
      operator: 'eq',
      value: false
    }
  ] as CrudFilters

  const { options: statusOptions } = useSelect<definitions['status']>({
    resource: 'status',
    fetchSize: 30,
    optionLabel: 'name',
    optionValue: 'id',
    sort: [
      {
        field: 'id',
        order: 'asc'
      }
    ],
    filters: [
      {
        field: 'id',
        operator: 'in',
        value: [0, 6]
      }
    ]
  })

  const {
    tableProps,
    tableQueryResult
    // isEditing
  } = useTable<definitions['supplements'], HttpError>({
    resource: 'supplements',
    initialSorter: [
      {
        field: 'volumetric',
        order: 'desc'
      },
      {
        field: 'id',
        order: 'desc'
      }
    ],
    permanentFilter: permFilters,
    initialPageSize: 4,
    queryOptions: {
      enabled: order !== undefined
    },
    syncWithLocation: false,
    metaData: {
      select: '*, status_id(*)'
    }
  })

  const getAllSupplements = async () => {
    const { data: suplements } = await supabaseClient.from('supplements')
      .select('*')
      .eq('order_id', order?.id)
      .eq('deleted', false)
    // setAllSupplements(suplements)
    return suplements
  }

  useEffect(() => {
    if (tableQueryResult.status === 'success') {
      setSupQuery(tableQueryResult)
    }
  }, [tableQueryResult])

  useEffect(() => {
    const fetchPermission = async () => {
      setHasPermission(await checkUserPermission())
    }
    fetchPermission()
  }, [])

  const handleRefresh = () => {
    tableQueryResult.refetch()
  }

  const handleAutomaticallyAdmittedVolumetric = (supplement: any) => {
    setPreparedVolumetricId(null)
    setAutomaticallyAdmittedVolumetric(supplement)
    setPreparingShipment(true)
    showProcess()
  }

  const closeProcessModal = () => {
    setAutomaticallyAdmittedVolumetric(null)
    setManualPreparationSupplement(null)
    setPreparingShipment(false)
    closeProcess()
  }

  const openProcessModal = (supplement?: any) => {
    if (warehouseMode && preparingShipment) return
    setPreparedVolumetricId(null)
    setManualPreparationSupplement(supplement || null)
    if (warehouseMode) setPreparingShipment(true)
    showProcess()
  }

  const orderForProcess = automaticallyAdmittedVolumetric
    ? {
        ...order,
        supplements: [
          ...(order?.supplements || []).filter((supplement: any) => supplement.id !== automaticallyAdmittedVolumetric.id),
          automaticallyAdmittedVolumetric
        ]
      }
    : order

  const {
    drawerProps: createDrawerProps,
    formProps: createFormProps,
    close: createClose,
    show: createShow
  } = useDrawerForm<definitions['supplements']>({
    action: 'create',
    resource: 'supplements',
    redirect: false
  })

  // const {
  //   drawerProps: createGroupedDrawerProps,
  //   formProps: createGroupedFormProps,
  //   close: createGroupedClose,
  //   show: createGroupedShow
  // } = useDrawerForm<definitions['supplements']>({
  //   action: 'create',
  //   resource: 'supplements',
  //   redirect: false
  // })

  const {
    formProps: editFormProps,
    drawerProps: editDrawerProps,
    show: editShow,
    close: editClose
  } = useDrawerForm<definitions['supplements']>({
    action: 'edit',
    resource: 'supplements',
    redirect: false
  })

  createFormProps.initialValues = {
    order_id: order?.id
  }

  editFormProps.initialValues = {
    ...editFormProps.initialValues,
    order_id: order?.id
  }

  const getConditionalClassName = (record: definitions['supplements']) => {
    if (record.parent) {
      return 'supplement-unified'
    }
    return ''
  }

  const createGroupedSupplement = async () => {
    setLoading(true)
    // get all suplements availables and create a new unified supplement
    const allSup = await getAllSupplements()

    // not sent that have pending status and not is a refund type
    const availableSup = allSup?.filter((x: Supplement) => x.description !== 'unified' && x.parent === null && x.email_sent_at === null).filter((x: Supplement) => !x.volumetric) || []

    let supplementInserted: Supplement | undefined

    if (availableSup.length === 1) {
      supplementInserted = availableSup[0]
      if (supplementInserted && supplementInserted.supplement && supplementInserted.supplement < 0) {
        await supabaseClient.from('refunds').insert({
          description: supplementInserted.description,
          amount: Math.abs(supplementInserted.supplement),
          status_id: ORDER_STATUS_ID_PENDING_PAYMENT,
          order_id: order?.id,
          deleted: false,
          type: 'client',
          updated_by: supabaseClient.auth.user()?.email
        })
      }
    } else if (availableSup.length > 1) {
      const finalSupplement = availableSup?.map((sup: Supplement) => sup.supplement || 0).reduce((acc, current) => acc + current, 0) || 0

      const state = {
        order_id: order?.id,
        volumetric: false,
        supplement: finalSupplement,
        description: 'unified',
        status_id: (finalSupplement > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        updated_by: supabaseClient.auth.user()?.email
      }
      // Save the supplement and change the other suplements, if is minor to 0, create a refund too
      const { data, error } = await supabaseClient.from('supplements')
        .insert(state)
        .single()
      if (error) {
        setErrorSaving(error)
      }

      if (data && data.id) {
        supplementInserted = data
        if (finalSupplement > 0) {
          const { error: supError } = await supabaseClient.from('supplements')
            .update({
              updated_by: supabaseClient.auth.user()?.email,
              parent: data?.id,
              read_by_user: true // to remove a duplicated notification
            })
            .in('id', availableSup.map((x: any) => x.id))

          if (supError) {
            setErrorSaving(supError)
            return
          }
        } else {
          const { error: supError } = await supabaseClient.from('supplements')
            .update({
              updated_by: supabaseClient.auth.user()?.email,
              parent: data?.id,
              read_by_user: true, // to remove a duplicated notification
              status_id: ADMITTED_STATUS
            })
            .in('id', availableSup.map((x: any) => x.id))

          if (supError) {
            setErrorSaving(supError)
            return
          }
        }
        // create the refund too if we have refunds produced.
        const { data: refundsData, error: refundsError } = await supabaseClient.from('refunds')
          .select('id, description, amount')
          .eq('order_id', order?.id)
          .eq('deleted', false)
        if (refundsError) {
          setErrorSaving(refundsError)
          return
        }
        const refundsToRemove = refundsData?.filter((x: any) => x.description === 'refundStore' || x.description === 'refundTransport' || availableSup.find((y: any) => y.reason === x.description))
        if (finalSupplement >= 0) {
          await supabaseClient.from('refunds').update({
            deleted: true,
            deleted_by: supabaseClient.auth.user()?.email || null,
            updated_by: supabaseClient.auth.user()?.email || null
          }).in('id', refundsToRemove.map((x: any) => x.id)).eq('status_id', PENDING_REFUND_PAYMENT_STATUS_ID)
        } else {
          if (refundsToRemove && refundsToRemove.length > 0) {
            await supabaseClient.from('refunds').update({
              deleted: true,
              deleted_by: supabaseClient.auth.user()?.email || null,
              updated_by: supabaseClient.auth.user()?.email || null
            }).in('id', refundsToRemove.map((x: any) => x.id)).eq('status_id', PENDING_REFUND_PAYMENT_STATUS_ID)
          }
          await supabaseClient.from('refunds').insert({
            description: 'unified',
            amount: Math.abs(finalSupplement),
            status_id: ORDER_STATUS_ID_PENDING_PAYMENT,
            order_id: order?.id,
            deleted: false,
            type: 'client',
            updated_by: supabaseClient.auth.user()?.email
          })
        }
      }
    }

    // Check if all supplements have admitted status and if the order have prcessed status or not
    const { data: supUpdated } = await supabaseClient.from('supplements')
      .select('id, status_id, description, supplement, created_at')
      .eq('order_id', order?.id)
      .eq('deleted', false)

    const { data: orderData } = await supabaseClient.from('orders')
      .select('id, status_id, market_orders(id, order_id, deleted)')
      .eq('id', order?.id)
      .single()

    if (orderData.status_id === SUPPLEMENT_STATUS && supUpdated?.filter((x: Supplement) => x.status_id === ORDER_STATUS_ID_PENDING_PAYMENT).length === 0) {
      const supUpdatedUnified = supUpdated?.filter((x: Supplement) => x.description === 'unified').sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())?.[0] || []
      const mo = orderData.market_orders?.filter((x: any) => x.deleted === false)

      // console.log(supUpdatedUnified, mo, supUpdated)

      if (supUpdatedUnified && supUpdatedUnified.supplement < 0 && mo.length > 0) {
        await supabaseClient.from('orders').update({ status_id: PROCESSED_STATUS }).eq('id', order?.id)
      } else if (supUpdatedUnified && supUpdatedUnified.supplement < 0 && mo.length <= 0) {
        await supabaseClient.from('orders').update({ status_id: ORDER_STATUS_ID_REQUESTED }).eq('id', order?.id)
      }
    }
    // Finally sent supplement email
    if (errorSaving === null) {
      await sendUnifiedSuplementEmail(supplementInserted)
    }

    setLoading(false)
    handleRefresh()
    refetchRef()
  }

  const sendUnifiedSuplementEmail = async (supplement: Supplement | undefined) => {
    if (supplement && supplement.email_sent_at === null) {
      // Prepare request
      const options: RequestInit = {
        method: 'post',
        headers: new Headers({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          type: 'INSERT',
          table: 'supplements',
          record: supplement,
          fromButton: true
        })
      }

      // Request to send email
      const response = await fetch(`${API_URL}/email`, options)

      if (response.ok) {
        const timeMoment = moment()

        await supabaseClient
          .from('supplements')
          .update({ email_sent_at: timeMoment, updated_by: supabaseClient.auth.user()?.email })
          .eq('id', supplement.id)

        supplement.email_sent_at = timeMoment.format(DEFAULT_DATE_TIME_FORMAT)
      }
    }
  }

  const checkPermission = () => {
    return hasPermission === true
  }

  return (
    <>
      <List
        title={t('order.fields.supplements')}
        breadcrumb={false}
        pageHeaderProps={{
          extra: (
            warehouseMode
              ? <>
              <CreateButton onClick={async () => {
                if (warehouseVolumetricBlock) {
                  showBlockingAdvice(warehouseVolumetricBlockedMessage)
                  return
                }
                if (await canOpenWarehouseVolumetric()) {
                  createShow()
                  if (hasWarehousePreparationNotice(order)) showPendingWarehouseNote()
                }
              }}>
                Añadir suplemento volumétrico
              </CreateButton>
              </>
              : <>
              {/* <CreateButton onClick={() => createGroupedShow()}> */}
              <CreateButton loading={loading} onClick={() => createGroupedSupplement()}>
                {t('order.actions.addUnifiedSupplement')}
              </CreateButton>
              <CreateButton onClick={() => createShow()}>
                {t('order.actions.addSupplement')}
              </CreateButton>
              <RefreshButton onClick={handleRefresh} />
            </>
          )
        }}
      >
        <Table {...tableProps}
          rowKey="id"
          rowClassName={(record) => getConditionalClassName(record)}
        >
          <Table.Column
            key="id"
            dataIndex="id"
            title={t('supplement.fields.id')}
            align='center'
          />
          <Table.Column
            key="weigth"
            dataIndex="weigth"
            title={t('supplement.fields.weigth')}
            align='center'
            sorter
          />
          <Table.Column
            key="length"
            dataIndex="length"
            title={t('supplement.fields.length')}
            align='center'
            sorter
          />
          <Table.Column
            key="heigth"
            dataIndex="heigth"
            title={t('supplement.fields.heigth')}
            align='center'
            sorter
          />
          <Table.Column
            key="depth"
            dataIndex="depth"
            title={t('supplement.fields.depth')}
            align='center'
            sorter
          />
          <Table.Column
            key="volume"
            dataIndex="volume"
            title={t('supplement.fields.volume')}
            align='center'
            sorter
          />
          <Table.Column
            key="billableVolume"
            dataIndex="billable_volume"
            title={t('supplement.fields.billableVolume')}
            align='center'
            sorter
          />
          <Table.Column
            key="weigthExcess"
            dataIndex="weigth_excess"
            title={t('supplement.fields.weigthExcess')}
            align='center'
            sorter
          />
          <Table.Column
            key="description"
            dataIndex="description"
            title={t('supplement.fields.description')}
            align='center'
            sorter
            render={(value) => SUPPLEMENT_PREDEFINED_OPTIONS.includes(value) ? t(`supplement.descriptions.${value}`) : value }
          />
          <Table.Column
            key="reason"
            dataIndex="reason"
            title={t('supplement.fields.reason')}
            align='center'
            sorter
          />
          <Table.Column
            key="supplement"
            dataIndex="supplement"
            title={t('supplement.fields.supplement')}
            render={(value) => currency(value)}
            align='center'
            sorter
          />
          <Table.Column
            dataIndex={['status_id', 'name']}
            align="center"
            title={t('supplement.fields.status')}
            width={(checkPermission() ? 200 : 50)}
            render={(value, data: definitions['supplements']) => {
              if (checkPermission()) {
                let newValue = value
                return (
                  <Space style={{ margin: 0 }}>
                    <Select
                      style={{ minWidth: 200 }}
                      options={statusOptions}
                      defaultValue={value}
                      filterOption={(input, option) => !!option?.label?.toLocaleLowerCase().includes(input.toLowerCase())}
                      onSelect={(val: any) => {
                        newValue = val
                      }}
                    />

                    <SaveButton
                      hideText
                      size="small"
                      onClick={async () => {
                        await supabaseClient.from('supplements').update({ status_id: newValue }).eq('id', data.id)
                        await handleRefresh()
                      }}
                    />
                  </Space>
                )
              } else {
                return (<Tag color={getColorLabel(value)}>
                  {capitalizeFirstLetter(value)}
                </Tag>)
              }
            }}
            sorter
          />
          <Table.Column<definitions['supplements']>
            key="sendEmail"
            dataIndex="sendEmail"
            title={t('supplement.fields.sendEmail')}
            render={(_, record) => <SendEmailButton supplement={record} refreshTable={handleRefresh} isSupplement={true} isEstimatedCost={false} />}
            align='center'
          />
          <Table.Column<definitions['supplements']>
            title={t('table.actions')}
            dataIndex="actions"
            align='center'
            render={(_, record: any) => (
              <Space>
                <EditButton
                  disabled={record.parent !== null || record.description === 'unified' || (warehouseMode && !isWarehouseVolumetricEditable(record))}
                  onClick={() => editShow(record.id)}
                  hideText
                />
                {record.volumetric && record.status_id?.id === ADMITTED_STATUS && <Space style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    disabled={warehouseMode && preparingShipment}
                    style={warehouseMode && (preparedVolumetricId === record.id || getWarehousePreparationBlock(order, record) === 'ALREADY_PREPARED')
                      ? { color: 'rgba(0, 0, 0, 0.25)', borderColor: '#d9d9d9', background: '#f5f5f5' }
                      : undefined}
                    onClick={() => {
                      const recordPreparationBlock = warehouseMode ? getWarehousePreparationBlock(order, record) : null
                      if (recordPreparationBlock || (warehouseMode && preparedVolumetricId === record.id)) {
                        showBlockingAdvice(t(preparedVolumetricId === record.id
                          ? 'supplement.warehouseBlock.alreadyPrepared'
                          : warehousePreparationAdviceKey(order, 'prepare')))
                        return
                      }
                      const currentYear = new Date().getFullYear()
                      const orderYear = new Date(order?.created_at!).getFullYear()
                      if (order?.status_id === ORDER_STATUS_ID_REQUESTED) {
                        setAdviseText(t('supplement.advise.requestedPrepare'))
                        showAdvice()
                      } else if (!warehouseMode && order?.shipments.find((x) => x.tracking_id)) {
                        setAdviseText(t('supplement.advise.duplicatedPrepare'))
                        showAdvice()
                      } else if (order?.cancelation_date !== null) {
                        setAdviseText(t('supplement.advise.cancellationRequestPrepare'))
                        showAdvice()
                      } else if (orderYear < currentYear) {
                        setAdviseText(t('supplement.advise.lastYear', { operation: t('supplement.advise.lastYearPrepare') }))
                        showAdvice()
                      } else if (order?.shipping_method.name.toLowerCase().includes('express')) {
                        setAdviseText(t('supplement.advise.dangerousWare'))
                        showAdvice()
                      } else {
                        openProcessModal(record)
                      }
                    }}
                  >
                    {t('shipments.actions.prepare')}
                  </Button>
                  <Button onClick={() => viewTicket(undefined, order?.id)}>
                    {t('shipments.actions.showTicket')}
                  </Button>
                </Space>}
                {!warehouseMode && (record.parent === null) && <ButtonLogicDelete click={() => {
                  show()
                  setId(record.id)
                }}
                />}
                <DeleteModal
                  modalProps={modalProps}
                  close={close}
                  id={id}
                  orderId={record.order_id}
                  entity='supplements'
                  tableQueryResult={tableQueryResult}
                />
              </Space>
            )}
          />
        </Table>
        {order && <>
          {/* <GroupedSupplementActions
            drawerProps={createGroupedDrawerProps}
            formProps={createGroupedFormProps}
            closeDrawer={createGroupedClose}
            getAllSupplements={getAllSupplements}
            tableProps={tableProps.dataSource}
            allSupplements={allSupplements?.filter((x: Supplement) => x.description !== 'unified' && x.parent === null) || []}
            tableQueryResult={tableQueryResult}
            orderId={order.id}
            orderQuery={orderQuery}
          /> */}
          <SupplementActions
            drawerProps={createDrawerProps}
            formProps={createFormProps}
            closeDrawer={createClose}
            tableQueryResult={tableQueryResult}
            orderId={order.id}
            shippingMethod={order.shipping_method}
            island={order.shipping_address?.island}
            orderQuery={orderQuery}
            packageNumber={order.package_number}
            order={order}
            onAutomaticallyAdmittedVolumetric={handleAutomaticallyAdmittedVolumetric}
            canAutomaticallyPrepareVolumetric={canAutomaticallyPrepareVolumetric}
            volumetricOnly={warehouseMode}
            warehouseReviewChecklistGuard={canOpenWarehouseVolumetric}
          />
          <SupplementActions
            edit={true}
            drawerProps={editDrawerProps}
            formProps={editFormProps}
            closeDrawer={editClose}
            tableQueryResult={tableQueryResult}
            orderId={order.id}
            shippingMethod={order.shipping_method}
            island={order.shipping_address?.island}
            orderQuery={orderQuery}
            packageNumber={order.package_number}
            order={order}
            onAutomaticallyAdmittedVolumetric={handleAutomaticallyAdmittedVolumetric}
            canAutomaticallyPrepareVolumetric={canAutomaticallyPrepareVolumetric}
          />
          <ProcessModal
            close={closeProcessModal}
            modalProps={modalPropsProcess}
            tableQuery={orderQuery}
            orderId={order?.id}
            currentOrderInfo={orderForProcess}
            warehouseMode={warehouseMode}
            directPreparation={warehouseMode && preparingShipment}
            preparationSupplement={automaticallyAdmittedVolumetric || manualPreparationSupplement}
            onPrepared={(supplementId) => setPreparedVolumetricId(supplementId || null)}
          />
          <AdviceModal
            modalProps={modalPropsAdvice}
            close={closeAdvice}
            show={showAdvice}
            infoText={adviseText}
            okFunction={openProcessModal}
          />
          <BlockingAdviceModal
            modalProps={modalPropsBlockingAdvice}
            close={closeBlockingAdvice}
            infoText={blockingAdviceText}
          />
          <PendingWarehouseNoteModal
            modalProps={modalPropsPendingWarehouseNote}
            close={closePendingWarehouseNote}
            warehouseNote={order?.warehouseNote || ''}
          />
        </>}
      </List>
    </>
  )
}
